import {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import IconTune from "@servicetitan/hammer-icon/mdi/round/tune.svg";
import IconClose from "@servicetitan/hammer-icon/mdi/round/close.svg";
import Search from "@servicetitan/hammer-icon/mdi/round/search.svg";
import {
  CustomFilter,
  Filter,
  MultiSelectFilter,
  SingleSelectFilter,
} from "./internal/types";
import { FilterGroupContext } from "./internal/FilterGroupContext";
import { Checkbox } from "../../../../components/Checkbox";
import { Radio } from "../../../../components/Radio";
import { Combobox } from "../../../../components/Combobox";
import { Flex } from "../../../../components/Flex";
import { useContainerQuery } from "../../../../internal/hooks";
import {
  checkActiveFilters,
  getActiveFilters,
  resetFilters,
  updateSingleFilter,
  cloneFiltersWithItemRefs,
  hasFilterSelectionChanged,
} from "./internal/utils/filter-state";
import { DateFieldSingle } from "../../../../components/DateFieldSingle";
import { DateFieldRange } from "../../../../components/DateFieldRange";
import ButtonToggle from "../../../../components/ButtonToggle";
import Chip from "../../../../components/Chip";
import Drawer from "../../../../components/Drawer";
import Button from "../../../../components/Button";

import { useToolbarSize } from "../internal/ToolbarContext";

import cx from "classnames";
import styles from "./Filters.module.scss";

/**
 * FilterDrawer component provides a drawer interface for filter selection.
 * Always operates in controlled mode with batch updates.
 * Built into FilterGroup for mobile and overflow filter editing.
 *
 * Features:
 * - Shows all available filters in a side panel
 * - Maintains separate draft state for all filters
 * - Updates filters only when Apply is clicked
 * - Provides Clear All functionality
 * - Supports boolean, custom, single-select, multi-select, date, and date range filter types
 * - Integrates with FilterGroup context for state management
 * - Handles date and date range filters with appropriate input components
 */
export const FilterDrawer = () => {
  const [open, setOpen] = useState(false);

  // Get filter context from parent
  const {
    filters,
    updateFilter,
    onFilterChange,
    hiddenFilters,
    filterGroupRef,
  } = useContext(FilterGroupContext);
  const toolbarSize = useToolbarSize();

  const containerQuery = useContainerQuery(filterGroupRef);
  const showInlineFilters =
    containerQuery &&
    containerQuery.name !== "xs" &&
    containerQuery.name !== "sm";

  // Local draft state for filters within the drawer
  const [draftFilters, setDraftFilters] = useState<Filter[]>(filters);

  // Local search value state for combobox search fields
  const [searchValues, setSearchValues] = useState<Record<string, string>>({});

  // Count active filters based on view mode
  const activeFiltersCount = getActiveFilters(
    !showInlineFilters ? filters : hiddenFilters,
  ).length;

  // Track previous state to detect when to sync draft
  const prevOpenRef = useRef(open);
  const prevFiltersRef = useRef<Filter[]>(filters);

  /**
   * Reset draft filters when drawer opens and sync when filter selections change while open
   * Ensures drawer always shows current filter state
   */
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;

    if (open) {
      // Sync draft state if:
      // 1. Drawer just opened, OR
      // 2. Any filter selection value changed while already open (race condition fix)
      // Don't sync when only items arrays change (preserves selections during search)
      const anySelectionChanged = prevFiltersRef.current.some(
        (prevFilter, index) => {
          const newFilter = filters[index];
          return newFilter && hasFilterSelectionChanged(prevFilter, newFilter);
        },
      );

      const shouldSync = justOpened || anySelectionChanged;

      if (shouldSync) {
        setDraftFilters(cloneFiltersWithItemRefs(filters));
      }

      prevFiltersRef.current = filters;
    }
  }, [open, filters]);

  /**
   * Handles changing a draft filter value
   * Updates only the draft state, not the actual filters
   *
   * @param filterId - ID of the filter to update
   * @param value - New value for the filter
   */
  const handleDraftChange = useCallback(
    (filterId: string, value: unknown) => {
      setDraftFilters((draft) => updateSingleFilter(draft, filterId, value));

      // Reset search when selection is made for filters with search
      const filter = filters.find((f) => f.id === filterId);
      if (
        (filter?.type === "singleSelect" || filter?.type === "multiSelect") &&
        filter.hasSearch &&
        value
      ) {
        // Clear the search value
        setSearchValues((prev) => ({ ...prev, [filterId]: "" }));
        // Reset the items list
        requestAnimationFrame(() => {
          filter.onSearch?.("");
        });
      }
    },
    [filters],
  );

  /**
   * Clears all draft filters in the drawer
   * Does not affect the actual filters until Apply is clicked
   */
  const clearDraftFilters = () => {
    setDraftFilters(resetFilters(draftFilters));
  };

  const hasActiveDraftFilters = checkActiveFilters(draftFilters);

  /**
   * Render filter form controls based on filter types
   * Delegates rendering to appropriate components based on filter type
   */
  const filterForm = useMemo(
    () =>
      draftFilters.map((draftFilter) => {
        // Find the original filter to get the render functions
        const originalFilter = filters.find((f) => f.id === draftFilter.id);
        if (!originalFilter) return null;

        switch (draftFilter.type) {
          case "boolean":
            return (
              <Checkbox
                key={draftFilter.id}
                label={draftFilter.label}
                checked={draftFilter.checked}
                onChange={(e) =>
                  handleDraftChange(draftFilter.id, e?.target.checked ?? false)
                }
              />
            );
          case "custom": {
            const customFilter = originalFilter as CustomFilter;
            return (
              <div key={draftFilter.id}>
                {customFilter.drawerRender?.({
                  value: draftFilter.value,
                  onChange: (value) => handleDraftChange(draftFilter.id, value),
                })}
              </div>
            );
          }
          case "singleSelect": {
            const singleSelectFilter = originalFilter as SingleSelectFilter<{
              id: string;
              label: string;
            }>;
            const draftSingleSelectFilter = draftFilter as SingleSelectFilter<{
              id: string;
              label: string;
            }>;

            if (singleSelectFilter.simpleDrawerVariant) {
              // Simple variant: Radio group
              return (
                <div key={draftFilter.id}>
                  <Radio.Group legend={singleSelectFilter.label}>
                    {singleSelectFilter.items.map((item) => (
                      <Radio
                        key={item.id}
                        name={`${singleSelectFilter.id}-radio`}
                        value={item.id}
                        label={item.label}
                        checked={
                          draftSingleSelectFilter.selectedItem?.id === item.id
                        }
                        onChange={(e) => {
                          if (e?.target.checked) {
                            const selected = singleSelectFilter.items.find(
                              (opt) => opt.id === e.target.value,
                            );
                            handleDraftChange(draftFilter.id, selected);
                          }
                        }}
                      />
                    ))}
                  </Radio.Group>
                </div>
              );
            } else {
              // Default variant: Combobox with search
              // Ensure selected item uses the reference from current items array
              const selectedItem = draftSingleSelectFilter.selectedItem
                ? (singleSelectFilter.items?.find(
                    (item) =>
                      item.id === draftSingleSelectFilter.selectedItem?.id,
                  ) ?? null)
                : null;

              return (
                <Combobox
                  key={draftFilter.id}
                  items={singleSelectFilter.items}
                  selectedItem={selectedItem}
                  onChange={(
                    selectedItem: { id: string; label: string } | null,
                  ) => {
                    handleDraftChange(draftFilter.id, selectedItem);
                  }}
                  itemToString={(item) => item?.label ?? ""}
                  disableFilter={singleSelectFilter.hasSearch}
                >
                  {singleSelectFilter.hasSearch ? (
                    <Combobox.SearchField
                      label={draftSingleSelectFilter.label}
                      prefix={{ icon: { svg: Search } }}
                      placeholder="Search..."
                      size="small"
                      value={
                        draftSingleSelectFilter.selectedItem?.label ||
                        searchValues[draftFilter.id] ||
                        ""
                      }
                      onChange={(e) => {
                        const searchValue = e.target.value;
                        setSearchValues((prev) => ({
                          ...prev,
                          [draftFilter.id]: searchValue,
                        }));
                        singleSelectFilter.onSearch?.(searchValue);
                      }}
                      onClear={() => {
                        // Clear our controlled search value
                        setSearchValues((prev) => ({
                          ...prev,
                          [draftFilter.id]: "",
                        }));
                        // Clear the selection
                        handleDraftChange(draftFilter.id, undefined);
                        // Reset the parent's items list
                        singleSelectFilter.onSearchClear?.();
                      }}
                      data-anv="filter-drawer-combobox-search"
                    />
                  ) : (
                    <Combobox.SelectTrigger
                      label={singleSelectFilter.label}
                      placeholder="Select an option..."
                      size="small"
                    />
                  )}
                  <Combobox.Content>
                    {({ items }) => (
                      <Combobox.List style={{ padding: 0 }}>
                        {items.map((item, i) => (
                          <Combobox.Item key={item.id} item={item} index={i}>
                            {item.label}
                          </Combobox.Item>
                        ))}
                      </Combobox.List>
                    )}
                  </Combobox.Content>
                </Combobox>
              );
            }
          }
          case "multiSelect": {
            const multiSelectFilter = originalFilter as MultiSelectFilter<{
              id: string;
              label: string;
            }>;
            const draftMultiSelectFilter = draftFilter as MultiSelectFilter<{
              id: string;
              label: string;
            }>;

            if (multiSelectFilter.simpleDrawerVariant) {
              // Simple variant: ListView
              return (
                <Checkbox.Group
                  key={draftFilter.id}
                  legend={multiSelectFilter.label}
                >
                  {multiSelectFilter.items.map((item) => (
                    <Checkbox
                      key={item.id}
                      label={item.label}
                      checked={
                        draftMultiSelectFilter.selectedItems?.some(
                          (selected) => selected.id === item.id,
                        ) ?? false
                      }
                      onChange={(e) => {
                        const currentSelected =
                          draftMultiSelectFilter.selectedItems || [];
                        if (e?.target.checked) {
                          // Add item to selection
                          handleDraftChange(draftFilter.id, [
                            ...currentSelected,
                            item,
                          ]);
                        } else {
                          // Remove item from selection
                          handleDraftChange(
                            draftFilter.id,
                            currentSelected.filter(
                              (selected) => selected.id !== item.id,
                            ),
                          );
                        }
                      }}
                    />
                  ))}
                </Checkbox.Group>
              );
            } else {
              // Default variant: Combobox with multiple selection
              const hasActiveSearch =
                searchValues[draftFilter.id] &&
                searchValues[draftFilter.id].length > 0;

              return (
                <Combobox
                  key={draftFilter.id}
                  items={multiSelectFilter.items}
                  selectedItems={draftMultiSelectFilter.selectedItems || []}
                  onChange={(
                    selectedItems: { id: string; label: string }[] | null,
                  ) => handleDraftChange(draftFilter.id, selectedItems)}
                  itemToString={(item) => item?.label ?? ""}
                  itemToKey={(item) => item?.id ?? ""}
                  disableFilter={multiSelectFilter.hasSearch}
                  multiple
                  disableCloseOnSelectItem={!hasActiveSearch}
                >
                  {multiSelectFilter.hasSearch ? (
                    <Combobox.SearchField
                      label={multiSelectFilter.label}
                      prefix={{ icon: { svg: Search } }}
                      placeholder="Search..."
                      size="small"
                      value={searchValues[draftFilter.id] || ""}
                      onChange={(e) => {
                        const searchValue = e.target.value;
                        setSearchValues((prev) => ({
                          ...prev,
                          [draftFilter.id]: searchValue,
                        }));
                        multiSelectFilter.onSearch?.(searchValue);
                      }}
                      onClear={() => {
                        // Clear our controlled search value
                        setSearchValues((prev) => ({
                          ...prev,
                          [draftFilter.id]: "",
                        }));
                        handleDraftChange(draftFilter.id, []);
                        multiSelectFilter.onSearchClear?.();
                      }}
                      data-anv="filter-drawer-combobox-search"
                    />
                  ) : (
                    <Combobox.SelectTrigger
                      label={multiSelectFilter.label}
                      placeholder="Select options..."
                      size="small"
                    />
                  )}
                  <Combobox.Content>
                    {({ items }) => (
                      <Combobox.List style={{ padding: 0 }}>
                        {items.map((item, i) => (
                          <Combobox.Item key={item.id} item={item} index={i}>
                            {item.label}
                          </Combobox.Item>
                        ))}
                      </Combobox.List>
                    )}
                  </Combobox.Content>
                </Combobox>
              );
            }
          }
          case "date": {
            return (
              <DateFieldSingle
                key={draftFilter.id}
                label={draftFilter.label}
                value={draftFilter.value as string | null}
                onChange={(change) => {
                  handleDraftChange(draftFilter.id, change.date);
                }}
                mode={draftFilter.mode}
              />
            );
          }
          case "dateRange": {
            return (
              <DateFieldRange
                key={draftFilter.id}
                label={draftFilter.label}
                value={
                  draftFilter.value as {
                    startDate: string | null;
                    endDate: string | null;
                  } | null
                }
                onChange={(change) => {
                  handleDraftChange(draftFilter.id, {
                    startDate: change.startDate,
                    endDate: change.endDate,
                  });
                }}
                mode={draftFilter.mode}
              />
            );
          }
          default:
            return null;
        }
      }),
    [draftFilters, filters, searchValues, handleDraftChange],
  );

  /**
   * Applies all draft filter changes to the actual filters
   * Updates filters individually then triggers batch update
   */
  const applyFiltering = () => {
    // Update all filters with their current draft values
    draftFilters.forEach((draftFilter) => {
      switch (draftFilter.type) {
        case "boolean":
          updateFilter(draftFilter.id, draftFilter.checked, false);
          break;
        case "singleSelect":
          updateFilter(
            draftFilter.id,
            (draftFilter as SingleSelectFilter).selectedItem,
            false,
          );
          break;
        case "multiSelect":
          updateFilter(
            draftFilter.id,
            (draftFilter as MultiSelectFilter).selectedItems,
            false,
          );
          break;
        case "date":
        case "dateRange":
        case "custom":
          updateFilter(draftFilter.id, draftFilter.value, false);
          break;
      }
    });

    // Submit all filters at once after updates
    onFilterChange?.(draftFilters);
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const hasHiddenActiveFilters = activeFiltersCount > 0;

  const triggerClasses = cx(
    styles["filter-button-trigger"],
    styles["filter-drawer-trigger"],
    {
      [styles["filter-drawer-trigger--chipped"]]: hasHiddenActiveFilters,
    },
  );

  return (
    <>
      <ButtonToggle
        data-anv="filter-group-drawer-trigger"
        icon={{ before: IconTune }}
        onClick={() => setOpen(true)}
        className={triggerClasses}
        checked={open}
        size={toolbarSize}
      >
        Filters
        {hasHiddenActiveFilters && (
          <Chip
            size="small"
            color="#0265DC"
            label={activeFiltersCount.toString()}
          />
        )}
      </ButtonToggle>

      <Drawer
        data-anv="filter-group-drawer"
        data-testid="filter-group-drawer"
        open={open}
        onClose={() => setOpen(false)}
      >
        <Drawer.Content>
          <Flex direction="column" gap={6} grow={1}>
            {filterForm}
          </Flex>
        </Drawer.Content>

        <Drawer.Footer>
          <Flex
            justifyContent={
              hasActiveDraftFilters ? "space-between" : "flex-end"
            }
            grow={1}
          >
            {hasActiveDraftFilters && (
              <Button
                appearance="ghost"
                icon={{ before: IconClose }}
                onClick={clearDraftFilters}
              >
                Clear All Filters
              </Button>
            )}

            <Flex gap="3">
              <Button onClick={handleCancel}>Cancel</Button>
              <Button appearance="primary" onClick={applyFiltering}>
                Apply
              </Button>
            </Flex>
          </Flex>
        </Drawer.Footer>
      </Drawer>
    </>
  );
};
